import { useId, useRef, useState } from 'react';

import {
  createImageObject,
  IMAGE_UPLOAD_MAX_FILE_SIZE,
  MAX_IMAGE_DIMENSION_SIZE,
  uploadImageToS3,
} from '@api/instant_survey';

import {
  Button,
  ImageIcon,
  Input,
  ModalV2 as Modal,
  toast,
  validateImageDimensions,
} from '@utilities';

import styles from './_index.module.scss';

interface ModalAddConceptProps {
  onClose: () => void;
  onCreate: (concept: { conceptImage: any | null; conceptName: string }) => void;
}

const ModalAddConcept: React.FC<ModalAddConceptProps> = ({
  onClose,
  onCreate,
}: ModalAddConceptProps) => {
  const addConceptInputId = useId();
  const conceptNameInputId = useId();
  const conceptImageInputRef = useRef<HTMLInputElement>(null);
  const [conceptImage, setConceptImage] = useState<any>(null);
  const [conceptName, setConceptName] = useState<string>('');
  const [isUploadingConcept, setIsUploadingConcept] = useState<boolean>(false);

  const conceptImageUrl = conceptImage?.cdnUrl || conceptImage?.previewUrl || null;
  const isCreateDisabled = !conceptName.trim() || isUploadingConcept;

  const handleClose = () => {
    if (isUploadingConcept) return;
    onClose();
  };

  const handleConceptCreate = () => {
    if (isCreateDisabled) return;

    onCreate({
      conceptImage,
      conceptName: conceptName.trim(),
    });
  };

  const handleConceptImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > IMAGE_UPLOAD_MAX_FILE_SIZE) {
      toast.error('File size exceeds 5MB limit');
      input.value = '';
      return;
    }

    try {
      await validateImageDimensions({ file, maxDimensionSize: MAX_IMAGE_DIMENSION_SIZE });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to read image dimensions');
      input.value = '';
      return;
    }

    setIsUploadingConcept(true);

    try {
      const imageObject = await createImageObject({ objectType: file.type.substring(6) });

      if (!imageObject.url.startsWith('http://s3mock')) {
        await uploadImageToS3({
          fields: imageObject.fields,
          file,
          url: imageObject.url,
        });
      }

      setConceptImage({
        id: imageObject.id,
        previewUrl: URL.createObjectURL(file),
      });
    } catch (error: any) {
      toast.error('Error uploading concept image');
      console.error('Error uploading concept image:', error);
    } finally {
      setIsUploadingConcept(false);
      input.value = '';
    }
  };

  const handleConceptImageDropzoneKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
    if (isUploadingConcept || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    conceptImageInputRef.current?.click();
  };

  return (
    <Modal
      buttons={[
        <Button key="cancel" onClick={handleClose} text="Cancel" variant="secondary" />,
        <Button
          isDisabled={isCreateDisabled}
          key="create"
          onClick={handleConceptCreate}
          text="Create"
        />,
      ]}
      isActive
      onClose={handleClose}
      size="large"
      title="Add Concept"
    >
      <div className={styles['add-concept-modal-content']}>
        <Input
          autoFocus
          className={styles['add-concept-name-input']}
          id={conceptNameInputId}
          isDisabled={isUploadingConcept}
          isRequired
          label="CONCEPT NAME"
          onChange={(value) => setConceptName(String(value))}
          placeholder="Enter text"
          value={conceptName}
        />

        <label
          aria-disabled={isUploadingConcept}
          aria-label={conceptImageUrl ? 'Change concept image' : 'Add concept image'}
          className={styles['add-concept-image-dropzone']}
          htmlFor={addConceptInputId}
          onKeyDown={handleConceptImageDropzoneKeyDown}
          role="button"
          tabIndex={isUploadingConcept ? -1 : 0}
        >
          {conceptImageUrl ? (
            <img
              alt={conceptName || 'Concept image'}
              className={styles['add-concept-image-preview']}
              src={conceptImageUrl}
            />
          ) : (
            <div className={styles['add-concept-image-empty']}>
              <ImageIcon />
              <span className={styles['add-concept-image-button']}>
                {isUploadingConcept ? 'Uploading...' : 'Add Image'}
              </span>
            </div>
          )}
        </label>
        <input
          accept="image/gif, image/jpeg, image/png"
          aria-hidden
          className={styles['add-concept-input']}
          data-testid="add-concept-image-input"
          disabled={isUploadingConcept}
          id={addConceptInputId}
          onChange={handleConceptImageUpload}
          ref={conceptImageInputRef}
          type="file"
        />
      </div>
    </Modal>
  );
};

export default ModalAddConcept;
